import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PiArrowClockwiseBold, PiCheckCircleFill, PiWarningCircleFill } from 'react-icons/pi';
import { Button } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { AI_BUTTON, AiDisclaimer, AiError, AiPlateIcon, AiThinking, aiErrorMessage, useAiStatus } from './aiShared';

type Review = {
    status: 'pronta' | 'revisar' | 'bloquear';
    summary: string;
    issues: { severity?: 'alta' | 'media' | 'baixa'; title?: string; detail?: string; refs?: string[] }[];
    ok: string[];
    generated_at: string;
    cached: boolean;
};

const STATUS = {
    pronta: { label: 'Pronta para enviar', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300' },
    revisar: { label: 'Revisar antes de enviar', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200' },
    bloquear: { label: 'Não enviar ainda', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300' },
};

const SEVERITY = {
    alta: 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-100',
    media: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100',
    baixa: 'border-gray-200 bg-gray-50 text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100',
};

export default function PurchaseOrderAiReviewButton({ shipmentId, className }: { shipmentId: number; className?: string }) {
    const ai = useAiStatus();
    const [open, setOpen] = useState(false);
    const gen = useMutation({
        mutationFn: async (refresh: boolean) =>
            (await api.post<{ data: Review }>(`/ai/shipments/${shipmentId}/purchase-order-review`, { refresh })).data.data,
    });

    if (!ai.data?.features.purchase_orders) return null;
    const r = gen.data;

    return (
        <>
            <button
                type="button"
                title="Confere filial, fornecedor, cores, quantidades, preço e containers antes de enviar"
                onClick={() => {
                    setOpen(true);
                    gen.mutate(false);
                }}
                className={cn(AI_BUTTON, 'px-3 py-2 text-sm', className)}
            >
                <AiPlateIcon className="size-5" /> Revisar com IA
            </button>
            <V2Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Revisão da PO com IA"
                description="Conferência antes de enviar ao fornecedor. Nada é alterado na PO."
                size="2xl"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Fechar
                        </Button>
                        <Button variant="outline" disabled={gen.isPending} onClick={() => gen.mutate(true)}>
                            <PiArrowClockwiseBold className="me-1.5 size-4" /> Revisar de novo
                        </Button>
                    </>
                }
            >
                {gen.isPending ? (
                    <AiThinking label="Conferindo filial, cores, quantidades e containers…" />
                ) : gen.isError ? (
                    <AiError message={aiErrorMessage(gen.error)} onRetry={() => gen.mutate(false)} />
                ) : r ? (
                    <div className="space-y-4 text-sm">
                        <div>
                            <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-bold', STATUS[r.status].cls)}>{STATUS[r.status].label}</span>
                            <p className="mt-2 text-xs leading-relaxed text-gray-700 dark:text-gray-200">{r.summary}</p>
                        </div>
                        {r.issues.length > 0 && (
                            <div className="space-y-2">
                                {r.issues.map((i, idx) => (
                                    <div key={idx} className={cn('rounded-lg border px-3 py-2', SEVERITY[i.severity ?? 'media'])}>
                                        <p className="flex items-center gap-1.5 text-xs font-semibold">
                                            <PiWarningCircleFill className="size-4 shrink-0" /> {i.title}
                                        </p>
                                        <p className="mt-0.5 text-xs opacity-90">{i.detail}</p>
                                        {(i.refs ?? []).length > 0 && (
                                            <p className="mt-1 flex flex-wrap gap-1">
                                                {i.refs!.map((ref) => (
                                                    <span key={ref} className="rounded bg-white/70 px-1.5 py-0.5 font-mono text-[10px] dark:bg-black/20">
                                                        {ref}
                                                    </span>
                                                ))}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {r.ok.length > 0 && (
                            <div>
                                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Conferido e certo</p>
                                <ul className="space-y-1 text-xs text-gray-700 dark:text-gray-200">
                                    {r.ok.map((o) => (
                                        <li key={o} className="flex gap-1.5">
                                            <PiCheckCircleFill className="mt-0.5 size-3.5 shrink-0 text-emerald-500" />
                                            {o}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <AiDisclaimer generatedAt={r.generated_at} cached={r.cached} />
                    </div>
                ) : null}
            </V2Modal>
        </>
    );
}
